import { useState, useEffect } from 'react';
import { toast } from "sonner";
import { Spinner } from 'react-bootstrap';
import { FaSearch, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';

// Services
import { 
  cancelOrder, 
  completeOrder, 
  deleteOrder, 
  failCompleteOrder, 
  getOrderById,
  getOrdersByDomainId, 
  partiallyCompleteOrder 
} from '../../../service/api/Order';
import { createTransaction } from '../../../service/api/TransactionService';
import useSelectedDomain from '../../../service/Hooks/useSelectedDomain';
import { useUserData } from '../../../service/api/useUserData';
import { useUser } from '@clerk/clerk-react';
import { checkUser } from '../../../service/api/AuthService';
import { useValidationSubmit } from '../../../service/Hooks/useValidationSubmit';
import { useLoader } from '../../../service/Hooks/useLoader';

// Composants
import OrderTableRow from './components/OrderTableRow';
import OrderFiltersSkeleton from './components/OrderFiltersSkeleton';
// import OrderTableRowSkeleton from './components/OrderTableRowSkeleton';

import OrderFilters from './components/OrderFilters';
import OrderPagination from './components/OrderPagination';
import OrderDetailsModal from '../Details/OrderDetailsModal';
import TransactionQuantityModal from '../../Transactions/transactionQuantite/TransactionQuantityModal';
import ConfirmationModal from '../../../Modal/ConfirmationModal';
import ValidationModal from '../../../Validations/ValidationModal';
import ValidationDetailModal from '../../../Validations/ValidationDetailModal';
import OrderForm from '../OrderForm';

// Utilitaires
import { VALIDATION_STATUS } from '../../../service/utils/validationStatus';
import TableSkeleton from '../../TableSkeleton';

/**
 * COMPOSANT PRINCIPAL TABLEAU DES ORDRES
 * Gère l'affichage et les interactions de la liste des ordres
 * @param {Object} props
 * @param {Array} props.initialOrders - Ordres initiaux (optionnel)
 */
export default function OrderTables({ initialOrders = [] }) {

   // ÉTATS SKELETONS & MESSAGES
  const [isFiltering, setIsFiltering] = useState(false);
  const [noResults, setNoResults] = useState(false);

  // HOOKS ET ÉTATS PRINCIPAUX
  const selectedDomain = useSelectedDomain();
  const { showLoader, hideLoader } = useLoader();
  const { getUserInfo } = useUserData();
  const { user } = useUser();
  const { submitValidation, isSubmitting } = useValidationSubmit();
  
  // États de gestion des données
  const [orders, setOrders] = useState(initialOrders);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // États de pagination et filtres
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(null);
  const [validationStatusFilter, setValidationStatusFilter] = useState(null);
  
  // États de gestion des modals
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderFormModal, setShowOrderFormModal] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState(null);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [showDeleteOrderModal, setShowDeleteOrderModal] = useState(false);
  const [isQuantityModalOpen, setIsQuantityModalOpen] = useState(false);
  const [transactionOrderId, setTransactionOrderId] = useState(null);
  
  // États de validation
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationEntityType, setValidationEntityType] = useState('');
  const [validationEntityId, setValidationEntityId] = useState(null);
  const [showValidationDetailModal, setShowValidationDetailModal] = useState(false);
  const [selectedValidationEntityId, setSelectedValidationEntityId] = useState(null);
  const [selectedValidationEntityType, setSelectedValidationEntityType] = useState('');
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  const [orderToSubmit, setOrderToSubmit] = useState(null);
  
  // États utilisateur et rôles
  const [role, setRole] = useState(null);
  const [domains, setDomains] = useState([]);
  const [selectedDomains, setSelectedDomainState] = useState(null);
  
  // États transaction
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [floatingError, setFloatingError] = useState(null);
  const [successfulTransactions, setSuccessfulTransactions] = useState(() => {
    const stored = localStorage.getItem("successfulTransactions");
    return stored ? JSON.parse(stored) : [];
  });

  // CONSTANTES DE CONFIGURATION
  const ordersPerPage = 10;
  const refreshInterval = 5000;

   // Remplace ta logique INTELLIGENTE (vers ligne 70) par ÇA :
  const filteredOrders = selectedStatusFilter
    ? orders.filter(o => o.status === selectedStatusFilter)
    : orders;

  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);


  const [filterLoading, setFilterLoading] = useState(false);


  // LOGIQUE D'AFFICHAGE FIXÉE
  const showSkeletons = loading && orders.length === 0;
  const showFilterSkeletons = filterLoading;
  const showNoResults = !loading && !filterLoading && filteredOrders.length === 0 && orders.length > 0;
  const showData = !loading && !filterLoading && filteredOrders.length > 0;

  //  FIX : Effet simplifié
  useEffect(() => {
    const hasFilters = selectedStatusFilter || validationStatusFilter !== null;
    setIsFiltering(hasFilters);
  }, [selectedStatusFilter, validationStatusFilter]);


  //  Message contextuel intelligent
  const getNoResultsMessage = () => {
    if (selectedStatusFilter) {
      return `Aucun ordre avec le statut "${selectedStatusFilter}"`;
    }
    if (validationStatusFilter !== null) {
      const labels = {
        [VALIDATION_STATUS.DRAFT]: "Brouillon",
        [VALIDATION_STATUS.PENDING]: "En attente",
        [VALIDATION_STATUS.VALIDATED]: "Validé",
        [VALIDATION_STATUS.REJECTED]: "Rejeté"
      };
      return `Aucun ordre avec validation "${labels[validationStatusFilter] || 'Inconnu'}"`;
    }
    return "Aucun ordre disponible dans ce domaine";
  };


  /**
   * EFFET: Chargement des données utilisateur et rôles
   */
  useEffect(() => {
    if (!user) return;

    const fetchUserData = async () => {
      try {
        const email = user.emailAddresses[0]?.emailAddress;
        const userData = await checkUser(email);
        
        if (userData?.domains) {
          setDomains(userData.domains);
          sessionStorage.setItem("domains", JSON.stringify(userData.domains));
        }
        
        if (userData?.isAdmin) {
          setRole("admin");
          setSelectedDomainState(null);
          sessionStorage.removeItem("selectedDomains");
        } else {
          const storedDomain = sessionStorage.getItem("selectedDomains");
          if (storedDomain) {
            const domainObj = JSON.parse(storedDomain);
            if (domainObj) {
              setSelectedDomainState(domainObj);
              setRole(domainObj.role);
            }
          } else if (userData?.domains?.length > 0) {
            const firstDomain = userData.domains[0];
            setSelectedDomainState(firstDomain);
            setRole(firstDomain.role);
            sessionStorage.setItem("selectedDomains", JSON.stringify(firstDomain));
          } else {
            setRole("manager");
          }
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des domaines et du rôle :", error);
        setRole("manager");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  /**
   * EFFET: Chargement périodique des ordres
   */
  useEffect(() => {
    if (!selectedDomain || !selectedDomain.id) return;

    let isMounted = true;
    setLoading(true);
    setError(null);

    const fetchOrders = async () => {
      try {
        const params = {};
        if (validationStatusFilter !== null) {
          params.status = validationStatusFilter;
        }
        
        const data = await getOrdersByDomainId(selectedDomain.id, params);
        if (isMounted) {
          setOrders(data);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError('Erreur lors de la récupération des données.',err);
          setLoading(false);
        }
      }
    };

    fetchOrders();
    const interval = setInterval(fetchOrders, refreshInterval);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [selectedDomain, validationStatusFilter]);

  /**
   * GESTIONNAIRES D'ACTIONS PRINCIPAUX
   */

  // Complétion d'ordre pour marché Private
  const handleCompleteOrder = async (orderId, order) => {
    if (order.targetMarketName === 'Private') {
      setIsTransactionModalOpen(true);
      setTransactionOrderId(orderId);

      const transactionData = {
        orderId: order.id,
        creatorId: order.creatorId,
        price: order.price,
        quantity: order.quantity,
      };

      try {
        showLoader({ message: "Complétion de l'ordre en cours...", indeterminate: true });
        await createTransaction(transactionData);
        const success = await completeOrder(orderId);
        
        if (success) {
          toast.success(`Transaction et complétion réussies pour l'ordre ${order.id} !`);
          persistSuccessfulTransaction(order.id);
          setOrders(prev =>
            prev.map(o =>
              o.id === order.id
                ? { ...o, status: "Completed", transactionCreated: true }
                : o
            )
          );
        }
      } catch (err) {
        toast.error("Échec de la transaction. L'ordre n'a pas été complété.");
        try {
          await failCompleteOrder(orderId);
        } catch (failErr) {
          toast.error("Echec lors de la mise à jour de l'ordre ",failErr);
        }
        setOrders(prev =>
          prev.map(o =>
            o.id === order.id ? { ...o, status: "Echec Completed" } : o
          )
        );
      } finally {
        hideLoader();
      }

      setTimeout(() => {
        setIsTransactionModalOpen(false);
        setTransactionOrderId(null);
      }, 5000);
    } else {
      toast.info("Veuillez cliquer sur 'Créer transaction' pour saisir la quantité.");
    }
  };

  // Création de transaction avec quantité
  const handleCreateTransactionWithQuantity = async (quantity, transactionPrice) => {
    try {
      const order = orders.find(o => o.id === transactionOrderId);
      const transactionData = {
        orderId: order.id,
        creatorId: order.creatorId,
        price: transactionPrice,
        quantity: quantity,
      };
      
      showLoader({ message: "Création de transaction en cours...", indeterminate: true });
      await createTransaction(transactionData);
      toast.success(`Transaction créée avec succès pour l'ordre ${order.id} !`);
      
      setOrders(prev =>
        prev.map(o =>
          o.id === order.id
            ? { 
                ...o, 
                // status: "Completed", 
                transactionCreated: true,
                updatedAt: new Date().toISOString()
              }
            : o
        )
      );
    } catch (error) {
      // console.error("Erreur lors de la création de la transaction :", error);
      const errorMessage = error.response?.data?.message 
        || error.message 
        || "Échec de la création de la transaction";
      
      toast.error(errorMessage);
      setOrders(prev =>
        prev.map(o =>
          o.id === transactionOrderId ? { 
            ...o, 
            status: "Failed",
            lastError: errorMessage
          } : o
        )
      );
    } finally {
      hideLoader();
      setIsQuantityModalOpen(false);
    }
  };

  // Complétion partielle
  const handlePartialOrder = async (orderId) => {
    try {
      showLoader({ message: "Mise à jour de l'ordre en cours...", indeterminate: true });
      const success = await partiallyCompleteOrder(orderId);
      if (success) {
        toast.info("L'ordre a été partiellement complété. Veuillez la completer en cliquant sur le bouton completer du menu");
        setOrders((prev) =>
          prev.map(o => o.id === orderId ? { ...o, status: "Partial" } : o)
        );
      }
    } catch (err) {
      toast.error(err.message || "Erreur lors de la complétion partielle.");
    } finally {
      hideLoader();
    }
  };

  // Annulation d'ordre
  const handleCancelOrder = async (orderId) => {
    try {
      showLoader({ message: "Mise à jour de l'ordre en cours...", indeterminate: true });
      const success = await cancelOrder(orderId);
      if (success) {
        toast.success("L'ordre a été annulé avec success.");
        setOrders((prev) =>
          prev.map(o => o.id === orderId ? { ...o, status: "Canceled" } : o)
        );
      }
    } catch (err) {
      toast.error(err.message || "Erreur lors de l'annulation de cette action.");
    } finally {
      hideLoader(); 
    }
  };

  // Suppression d'ordre
  const handleDeleteOrderClick = (order) => {
    setOrderToDelete(order);
    setShowDeleteOrderModal(true);
  };

  const handleConfirmDeleteOrder = async () => {
    try {
      showLoader({ message: "Suppression de l'ordre en cours...", indeterminate: true });
      const success = await deleteOrder(orderToDelete.id);
      if (success) {
        toast.success("L'ordre a été supprimé avec succès !");
        setOrders((prev) => prev.filter(o => o.id !== orderToDelete.id));
      }
    } catch (err) {
      toast.error(err.message || "Erreur lors de la suppression.");
    } finally {
      hideLoader();
      setOrderToDelete(null);
      setShowDeleteOrderModal(false);
    }
  };

  // Édition d'ordre
  const handleShowEditOrderModal = async (order) => {
    if (!order) {
      toast.error("Aucune donnée d'ordre fournie.");
      return;
    }
    
    if (order.validationStatut === VALIDATION_STATUS.VALIDATED || order.validationStatut === VALIDATION_STATUS.EXECUTED) {
      toast.error("Cet ordre ne peut pas être modifié dans son état actuel.");
      return;
    }
    
    try {
      const completeOrderData = await getOrderById(order.id);
      setOrderToEdit(completeOrderData);
      setShowOrderFormModal(true);
      
      setTimeout(() => {
        const modal = new window.bootstrap.Modal(document.getElementById('newOrder'));
        modal.show();
      }, 100);
      
    } catch (error) {
      console.error('Erreur lors de la récupération des données:', error);
      toast.error("Erreur lors du chargement des données de l'ordre");
    }
  };

  // Visualisation des détails
  const handleViewOrder = async (orderId) => {
    try {
      const transactionDetails = await getOrderById(orderId);
      setSelectedOrder(transactionDetails);
      setShowModal(true);
    } catch (err) {
      toast.error("Erreur lors de la récupération des détails de l'ordre.");
    }
  };

  // Soumission à validation
  const handleConfirmSubmit = async () => {
    if (!orderToSubmit) return;

    try {
      const userInfo = await getUserInfo();
      if (!userInfo) {
        toast.error("Impossible de récupérer l'utilisateur(id) qui soumet l'action");
        return;
      }

      const success = await submitValidation(orderToSubmit.id, userInfo.accountId);

      if (success) {
        const data = await getOrdersByDomainId(selectedDomain.id);
        setOrders(data || []);
      }

      setShowSubmitConfirmation(false);
    } catch (error) {
      // toast.error("Erreur lors de la soumission de l'order :", error);

    }
  };

  /**
   * UTILITAIRES LOCAUX
   */
  const persistSuccessfulTransaction = (orderId) => {
    setSuccessfulTransactions((prev) => {
      const updated = [...prev, orderId];
      localStorage.setItem("successfulTransactions", JSON.stringify(updated));
      return updated;
    });
  };

  const isCurrentUserValidator = selectedDomains?.isValidator;

  // /**
  //  * CALCULS POUR L'AFFICHAGE
  //  */
  // const filteredOrders = selectedStatusFilter
  //   ? orders.filter(o => o.status === selectedStatusFilter)
  //   : orders;

  // const indexOfLastOrder = currentPage * ordersPerPage;
  // const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  // const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  // const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  /**
   * GESTIONNAIRES D'INTERFACE
   */
  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  
  //FILTRES avec LOADING
  const handleStatusFilterChange = async (status) => {
    setFilterLoading(true);
    setSelectedStatusFilter(status);
    setCurrentPage(1);
    setTimeout(() => {
      setFilterLoading(false);
      setNoResults(filteredOrders.length === 0);
    }, 800);
  };

  const handleValidationStatusFilterChange = async (status) => {
    setFilterLoading(true);
    setValidationStatusFilter(status);
    setCurrentPage(1);
    setTimeout(() => setFilterLoading(false), 800);
  };

  const resetAllFilters = () => {
    setSelectedStatusFilter(null);
    setValidationStatusFilter(null);
    setCurrentPage(1);
  };

  const handleCloseModal = () => setShowModal(false);
  const handleCloseOrderFormModal = () => {
    setShowOrderFormModal(false);
    setOrderToEdit(null);
  };

  const handleOrderUpdated = () => {
    if (selectedDomain?.id) {
      const fetchOrders = async () => {
        try {
          const data = await getOrdersByDomainId(selectedDomain.id);
          setOrders(data || []);
        } catch (err) {
          console.error("Erreur lors du rechargement des ordres:", err);
        }
      };
      fetchOrders();
    }
    handleCloseOrderFormModal();
  };

  const handleCancelDeleteOrder = () => {
    setOrderToDelete(null);
    setShowDeleteOrderModal(false);
  };

  const handleCloseValidationModal = () => {
    setShowValidationModal(false);
    setValidationEntityId(null);
    setValidationEntityType('');
  };

  const handleValidationSuccess = () => {
    if (!selectedDomain || !selectedDomain.id) return;

    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getOrdersByDomainId(selectedDomain.id);
        setOrders(data || []);
        setLoading(false);
      } catch (err) {
        setError(err.message || "Impossible de rafraîchir les Ordres après validation.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  };

  const handleOpenValidationDetails = (entityId, entityType = 'Order') => {
    setSelectedValidationEntityId(entityId);
    setSelectedValidationEntityType(entityType);
    setShowValidationDetailModal(true);
  };

  const handleOpenSubmitConfirmation = (order) => {
    setOrderToSubmit(order);
    setShowSubmitConfirmation(true);
  };

  const handleOpenQuantityModal = (order) => {
    setTransactionOrderId(order.id);
    setIsQuantityModalOpen(true);
  };

  /**
 * Fonction pour recharger les ordres
 */
  const reloadOrders = async () => {
    if (!selectedDomain?.id) return;
    setLoading(true);
    setError(null);
    setNoResults(false);
    
    try {
      const params = {};
      if (validationStatusFilter !== null) {
        params.status = validationStatusFilter;
      }
      
      const data = await getOrdersByDomainId(selectedDomain.id, params);
      setOrders(data);
      setNoResults(data.length === 0 && (selectedStatusFilter || validationStatusFilter !== null));
    } catch (err) {
      setError(err.message || "Erreur lors du chargement des ordres");
    } finally {
      setLoading(false);
    }
  };

  // Calculate total results for display in OrderFilters
  const totalResults = filteredOrders.length;

  // Validation options for no results message
  const validationOptions = [
    { value: VALIDATION_STATUS.DRAFT, label: "Brouillon" },
    { value: VALIDATION_STATUS.PENDING, label: "En attente" },
    { value: VALIDATION_STATUS.VALIDATED, label: "Validé" },
    { value: VALIDATION_STATUS.REJECTED, label: "Rejeté" }
  ];

  // RENDU INTELLIGENT
  const renderContent = () => {
    if (showSkeletons) {
      return <div className="py-5"><OrderFiltersSkeleton /><TableSkeleton columns={10} rows={6} /></div>;
    }

    if (showFilterSkeletons) {
    return (
      <div className="py-4 position-relative">
        <div className="skeleton-overlay-filter" />
        <OrderFilters
          disabled={true}
          selectedStatusFilter={selectedStatusFilter}
          validationStatusFilter={validationStatusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          onValidationStatusFilterChange={handleValidationStatusFilterChange}
          onResetFilters={resetAllFilters}
          totalResults={totalResults}
          isFiltered={isFiltering}
        />
        <TableSkeleton columns={10} rows={5} />
      </div>
    );
  }

    // 2. ERREUR (améliorée)
    if (error) {
      return (
        <div className="text-center py-5">
          <div className="rounded-3 px-4 py-5 mx-auto border border-danger-subtle bg-danger-subtle">
            <FaExclamationTriangle className="text-danger mb-3 fs-1" />
            <h4 className="text-danger fw-bold mb-3">Erreur de chargement</h4>
            <p className="text-muted mb-4 lead">{error}</p>
            
            <button 
              className="btn btn-primary rounded-pill px-4 py-2 mb-3"
              onClick={reloadOrders}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Rechargement...
                </>
              ) : (
                <>
                  <i className="fas fa-redo me-2"></i>
                  Réessayer
                </>
              )}
            </button>
            
            <div className="mt-4">
              <small className="text-muted">
                <FaInfoCircle className="me-1" />
                <strong>Suggestions:</strong> Vérifiez connexion, permissions, ou contactez support
              </small>
            </div>
          </div>
        </div>
      );
    }

    //  3. AUCUN RÉSULTAT (contextuel)
    if (showNoResults) {
      return (
        <div className="text-center py-5">
          <FaSearch className="text-muted mb-4 fs-1 opacity-50" />
          <h4 className="fw-bold text-muted mb-3">
            {selectedStatusFilter 
              ? `Aucun ordre "${selectedStatusFilter}" trouvé`
              : validationStatusFilter !== null
              ? `Aucun ordre avec validation "${validationOptions.find(o => o.value === validationStatusFilter)?.label}"`
              : "Aucun ordre correspondant"
            }
          </h4>
          <button className="btn btn-outline-light rounded-pill px-4" onClick={resetAllFilters}>
            Réinitialiser les filtres
          </button>
        </div>
      );
    }

     // 4. DONNÉES NORMALES
    return (
      <>
        <OrderFilters
          selectedStatusFilter={selectedStatusFilter}
          validationStatusFilter={validationStatusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          onValidationStatusFilterChange={handleValidationStatusFilterChange}
          onResetFilters={resetAllFilters}
          totalResults={totalResults}
          isFiltered={isFiltering}
        />
        
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead className="table-light rounded-2">
              <tr>
                <th>Titres</th>
                <th>Marché</th>
                <th>Émetteur</th>
                <th className="text-end">Type ordre</th>
                <th className="text-end">Quantité</th>
                <th className="text-center">Prix</th>
                <th className="text-center">Statut</th>
                <th className="text-center">Validation</th>
                <th className="text-center">Date</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentOrders.map((order) => (
                <OrderTableRow
                  key={order.id}
                  order={order}
                  role={role}
                  isCurrentUserValidator={isCurrentUserValidator}
                  onViewOrder={handleViewOrder}
                  onEditOrder={handleShowEditOrderModal}
                  onDeleteOrder={handleDeleteOrderClick}
                  onCompleteOrder={handleCompleteOrder}
                  onPartialOrder={handlePartialOrder}
                  onCancelOrder={handleCancelOrder}
                  onOpenQuantityModal={handleOpenQuantityModal}
                  onOpenValidationDetails={handleOpenValidationDetails}
                  onOpenSubmitConfirmation={handleOpenSubmitConfirmation}
                />
              ))}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && (
          <OrderPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </>
    );
  };

  /**
   * RENDU DU COMPOSANT
   */
  return (
    <>
      <div className="col-lg-12">
        <div className="search-filter-container mt-2 mb-4">
          <h2 className="card-title mb-1 fw-bold text-primary-emphasis text-center text-uppercase fs-2" 
              style={{ letterSpacing: "0.10em" }}>
            Liste des ordres
          </h2>
        </div>
        
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body py-3">
                {renderContent()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TOUS TES MODALS EXISTANTS (inchangés) */}
      {isTransactionModalOpen && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Transaction en cours pour l'ordre {transactionOrderId}, veuillez patienter...</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour les détails de l'ordre */}
      <OrderDetailsModal
        show={showModal}
        onHide={handleCloseModal}
        order={selectedOrder}
        loading={false}
      />

      {/* Modal de quantité pour transaction */}
      <TransactionQuantityModal
        show={isQuantityModalOpen}
        onHide={() => setIsQuantityModalOpen(false)}
        onConfirm={handleCreateTransactionWithQuantity}
        order={orders.find(o => o.id === transactionOrderId)}
        loading={false}
      />

      {/* Modal de confirmation de suppression */}
      {showDeleteOrderModal && (
        <ConfirmationModal
          message="Êtes-vous sûr de vouloir supprimer cet ordre ?"
          onConfirm={handleConfirmDeleteOrder}
          onCancel={handleCancelDeleteOrder}
        />
      )}

      {/* Modal de validation */}
      <ValidationModal
        show={showValidationModal}
        onClose={handleCloseValidationModal}
        entityId={validationEntityId}
        entityType={validationEntityType}
        onValidationSuccess={handleValidationSuccess}
      />

      {/* Modal des détails de validation */}
      {showValidationDetailModal && (
        <ValidationDetailModal
          entityId={selectedValidationEntityId}
          entityType={selectedValidationEntityType}
          isValidator={isCurrentUserValidator}
          onClose={() => setShowValidationDetailModal(false)}
          onApprove={() => {}}
          onReject={() => {}}
        />
      )}

      {/* Modal de confirmation de soumission */}
      {showSubmitConfirmation && (
        <ConfirmationModal
          message={`Êtes-vous sûr de vouloir soumettre l'ordre #${orderToSubmit?.id} à la validation ?`}
          onConfirm={handleConfirmSubmit}
          onCancel={() => setShowSubmitConfirmation(false)}
          confirmText="Soumettre"
          confirmVariant="primary"
          isLoading={isSubmitting}
        />
      )}

      {/* Modal d'édition d'ordre */}
      {showOrderFormModal && (
        <OrderForm
          orderToEdit={orderToEdit}
          onClose={handleCloseOrderFormModal}
          onOrderUpdated={handleOrderUpdated}
        />
      )}
    </>
  );
}